import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";

const defaultSouthDakotaResources = [
  { name: "My Safety Plan", url: "https://mysafetyplan.org" },
  { name: "South Dakota Suicide Prevention", url: "https://www.sdsuicideprevention.org/" },
  { name: "Department of Social Services", url: "https://dss.sd.gov/behavioralhealth/default.aspx" },
  { name: "seizetheawkward", url: "https://seizetheawkward.org/" },
  { name: "liveinspired365", url: "https://liveinspired365.org/" },
  { name: "journeyofhopesd", url: "https://www.journeyofhopesd.org/" },
  { name: "sdbehavioralhealth", url: "https://sdbehavioralhealth.gov/recovery/peer-support" },
  { name: "South Dakota National Alliance on Mental Health", url: "https://namisouthdakota.org/" },
  { name: "Peer Support", url: "https://sdbehavioralhealth.gov/recovery/peer-support" },
];

const defaultNorthDakotaResources = [
  { name: "hhs.nd.gov", url: "https://www.hhs.nd.gov/news/health-and-human-services-recognizes-mental-health-awareness-month-shares-resources-support" },
  { name: "Veterans", url: "https://www.veterans.nd.gov/behavioral-mental-health" },
  { name: "mhand", url: "https://www.mhand.org/" },
  { name: "ndthrives", url: "https://ndthrives.com/access/" },
  { name: "hhs.nd.gov", url: "https://www.hhs.nd.gov/behavioral-health/directory" },
  { name: "nami north dakota", url: "https://naminorthdakota.org/" },
];

const defaultNationwideResources = [
  { name: "The National Widowers' Organization", url: "https://nationalwidowers.org/" },
  { name: "Hopenet", url: "https://hopenet.org/" },
  { name: "lyrahealth", url: "https://www.lyrahealth.com/resources/mental-health-stigma/" },
  { name: "Safety plan", url: "https://www.mysafetyplan.org/" },
  { name: "National Alliance on Mental Illness", url: "https://nami.org" },
  { name: "National Institute for Mental Health", url: "https://www.nimh.nih.gov/health/find-help" },
  { name: "Veterans Crisis Line", url: "https://www.veteranscrisisline.net/" },
  { name: "Substance Abuse and Mental Health Services Administration (SAMHSA)", url: "https://www.samhsa.gov/find-support" },
  { name: "How to Overcome Stigma in the workplace", url: "https://www.lyrahealth.com/resources/mental-health-stigma/" },
  { name: "Dbs Alliance", url: "https://www.dbsalliance.org/" },
];

const defaultContent = {
  heroTitle: "RESOURCES",
  breadcrumb: "Home / Resources",
  southDakotaResources: defaultSouthDakotaResources,
  northDakotaResources: defaultNorthDakotaResources,
  nationwideResources: defaultNationwideResources,
  quote1Text: "There is hope, even when your brain tells you there isn't.",
  quote1Author: "John Green",
  quote1Gradient: "from-[#e6ac00] via-[#f0a53a] to-[#f38e67]",
  quote2Text: "What mental health needs is more sunlight, more candor, and more unashamed conversation.",
  quote2Author: "Glenn Close",
  quote2Gradient: "from-[#50a7bf] via-[#59b7a9] to-[#7cc87d]",
  bottomImage: "/upload/mentalhealth.jpg",
  namiTitle: "nami",
  namiSubtitle: "National Alliance on Mental Illness",
  namiDescription: "The NAMI HelpLine provides the one-on-one help and information necessary to tackle tough challenges.",
  namiCall: "1-800-950-NAMI",
  namiChat: "Text \"HelpLine\" to 62640",
  namiCrisis: "Call or Text 988",
  crisis988Title: "988",
  crisis988Subtitle: "SUICIDE & CRISIS LIFELINE",
  crisis988Description: "We can help prevent suicide. The 988 Lifeline provides 24/7, free and confidential support for people in distress, prevention and crisis resources for you or your loved ones, and best practices for professionals in the United States.",
  subscribeHeading: "Get The Latest Updates",
  subscribePlaceholder: "Enter your email",
  subscribeButtonText: "Subscribe",
};

function ResourceList({ items }) {
  return (
    <div className="border-t border-gray-200">
      {items.map((item, index) => (
        <div
          key={index}
          className="border-b border-gray-200 py-2.5 text-[11px] md:text-[12px] text-gray-700"
        >
          <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#e84fa5] transition">
            {item.name}
          </a>
        </div>
      ))}
    </div>
  );
}

function Resources() {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "mentalHealthContent"));
        if (snap.exists()) {
          const data = snap.data();
          setContent((prev) => ({
            ...prev,
            ...data,
            southDakotaResources: data.southDakotaResources || defaultSouthDakotaResources,
            northDakotaResources: data.northDakotaResources || defaultNorthDakotaResources,
            nationwideResources: data.nationwideResources || defaultNationwideResources,
          }));
        }
      } catch (e) {}
      setLoading(false);
    };
    fetchContent();
  }, []);

  return (
    <div className="min-h-screen bg-white text-black">
      {/* HERO */}
      <section className="bg-[#f7d9ea]">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 h-[115px] flex flex-col items-center justify-center text-center">
          <h1 className="text-[22px] md:text-[28px] font-extrabold tracking-wide">
            {content.heroTitle}
          </h1>
          <p className="mt-1 text-[10px] md:text-[12px] text-gray-500">
            {content.breadcrumb}
          </p>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1180px] mx-auto px-4 md:px-8 pt-14 pb-20">
        <div className="grid lg:grid-cols-[1fr_250px] gap-10 lg:gap-8">
          {/* LEFT SIDE */}
          <div>
            {/* Top Two Columns */}
            <div className="grid md:grid-cols-2 gap-10 md:gap-16">
              <div>
                <h2 className="text-[14px] md:text-[16px] font-extrabold tracking-wide uppercase mb-3">
                  South Dakota
                </h2>
                <ResourceList items={content.southDakotaResources} />
              </div>

              <div>
                <h2 className="text-[14px] md:text-[16px] font-extrabold tracking-wide uppercase mb-3">
                  North Dakota
                </h2>
                <ResourceList items={content.northDakotaResources} />
              </div>
            </div>

            {/* Nationwide Section */}
            <div className="mt-8 md:mt-10">
              <h2 className="text-[14px] md:text-[16px] font-extrabold tracking-wide uppercase mb-3">
                Nationwide / USA-Wide (General)
              </h2>
              <ResourceList items={content.nationwideResources} />
            </div>

            {/* Quote Cards */}
            <div className="mt-8 md:mt-10 grid md:grid-cols-2 gap-6 max-w-[760px]">
              <div className={`h-[150px] md:h-[160px] rounded-sm bg-gradient-to-r ${content.quote1Gradient} text-white flex items-center justify-center px-8 text-center`}>
                <div>
                  <div className="text-[28px] leading-none mb-1">"</div>
                  <p className="text-[13px] md:text-[16px] font-extrabold leading-[1.35] uppercase tracking-wide">
                    {content.quote1Text}
                  </p>
                  <p className="mt-3 text-[9px] md:text-[10px] font-semibold">
                    — {content.quote1Author}
                  </p>
                </div>
              </div>

              <div className={`h-[150px] md:h-[160px] rounded-sm bg-gradient-to-r ${content.quote2Gradient} text-white flex items-center justify-center px-8 text-center`}>
                <div>
                  <div className="text-[28px] leading-none mb-1">"</div>
                  <p className="text-[13px] md:text-[16px] font-extrabold leading-[1.35] uppercase tracking-wide">
                    {content.quote2Text}
                  </p>
                  <p className="mt-3 text-[9px] md:text-[10px] font-semibold">
                    — {content.quote2Author}
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom image */}
            <div className="mt-10">
              {content.bottomImage && (
                <img src={content.bottomImage} alt="" className="object-contain" />
              )}
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <aside className="pt-1">
            <div className="space-y-4">
              {/* NAMI Card */}
              <a href="https://nami.org" target="_blank" rel="noopener noreferrer" className="block border border-gray-200 p-3 bg-white hover:shadow-md transition">
                <div className="bg-[#1f6aa8] text-white px-4 py-5">
                  <div className="text-center">
                    <div className="text-[28px] font-extrabold tracking-tight leading-none">
                      {content.namiTitle}
                    </div>
                    <div className="text-[10px] mt-1 opacity-95">
                      {content.namiSubtitle}
                    </div>
                  </div>

                  <div className="mt-5 text-[10px] leading-[1.5]">
                    <p className="font-semibold text-[12px] mb-2">
                      NAMI HELPLINE
                    </p>
                    <p>
                      {content.namiDescription}
                    </p>

                    <p className="mt-3 font-semibold">Call</p>
                    <p>{content.namiCall}</p>

                    <p className="mt-3 font-semibold">Chat or Text</p>
                    <p>{content.namiChat}</p>

                    <p className="mt-3 font-semibold">In Crisis?</p>
                    <p>{content.namiCrisis}</p>
                  </div>
                </div>
              </a>

              {/* 988 Card */}
              <a href="https://988lifeline.org/" target="_blank" rel="noopener noreferrer" className="block border border-gray-200 p-3 bg-white hover:shadow-md transition">
                <div className="bg-[#232a64] text-white px-4 py-5 text-center">
                  <div className="text-[46px] font-extrabold leading-none">
                    {content.crisis988Title}
                  </div>
                  <div className="mt-2 text-[20px] font-bold tracking-[0.18em] leading-tight">
                    {content.crisis988Subtitle}
                  </div>
                </div>

                <div className="mt-3 text-[#6aa0d0] text-[11px] leading-[1.35]">
                  <p className="font-semibold text-[12px] text-[#4a8dc4]">
                    988 Suicide &amp; Crisis Lifeline
                  </p>
                  <p className="mt-1">
                    {content.crisis988Description}
                  </p>
                </div>
              </a>
            </div>
          </aside>
        </div>
      </main>

      {/* SUBSCRIBE BAR */}
      <section className="bg-[#f4f4f4] border-t border-gray-200">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-5">
            <h3 className="text-[12px] md:text-[14px] font-extrabold tracking-wide uppercase">
              {content.subscribeHeading}
            </h3>

            <div className="flex w-full md:w-auto items-center">
              <input
                type="email"
                placeholder={content.subscribePlaceholder}
                className="h-9 md:h-10 w-full md:w-[215px] border border-gray-300 bg-white px-3 text-[11px] outline-none"
              />
              <button className="h-9 md:h-10 px-5 bg-black text-white text-[10px] md:text-[11px] font-semibold uppercase">
                {content.subscribeButtonText}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Resources;
